import { api, ApiError, applyTenantLogo, toast } from '@bemor/web-kit';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    ConfirmDialog,
    FileUpload,
    TenantLogo,
    TenantTheme,
} from '@bemor/web-kit/ui';
import { useState, type ComponentProps } from 'react';

import type { Tenant } from '@/api/types';
import { useWrite } from '@/lib/queries';
import { brandName } from '@/lib/tenants';

type TenantBrand = NonNullable<ComponentProps<typeof TenantLogo>['brand']>;

/** What Identity takes (it sniffs the content again; an SVG is sanitised or refused). */
export const LOGO_TYPES = ['image/svg+xml', 'image/png', 'image/webp'];
export const LOGO_MAX_BYTES = 512 * 1024;

/** The upload and remove refusals in words a person can act on. */
export function logoErrorMessage(error: unknown): string {
    const key = ApiError.is(error) ? error.key : null;
    switch (key) {
        case 'logo_unsafe':
            return 'This SVG has scripts or external references; export it again as a plain SVG or a PNG.';
        case 'logo_type':
            return 'Use an SVG, PNG or WebP image.';
        case 'logo_too_large':
            return 'The logo is over 512 KB; export a smaller file.';
        case 'logo_empty':
            return 'The file is empty; choose the logo again.';
        default:
            return 'The logo could not be saved. Try again.';
    }
}

const logoOf = (t: Tenant): string | null =>
    Array.isArray(t.brand)
        ? null
        : ((t.brand as { logo_url?: string | null }).logo_url ?? null);

/**
 * BM-46 §2.2: the tenant's logo (white-label): shown in the header's fixed box and on the checkout's brand panel,
 * uploaded (SVG, PNG or WebP up to 512 KB) or removed (the apps then show the name). A change on this page's own
 * tenant shows in the header and the favicon at once.
 */
export function LogoSection({ tenant }: { tenant: Tenant }) {
    const [file, setFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [confirm, setConfirm] = useState(false);
    const logoUrl = logoOf(tenant);
    const brand: TenantBrand = {
        ...(Array.isArray(tenant.brand) ? {} : tenant.brand),
        display_name: brandName(tenant),
        logo_url: logoUrl,
    } as TenantBrand;
    const inv = [`/staff/tenants`];
    const done = (next: Tenant, message: string) => {
        applyTenantLogo(next.slug, logoOf(next));
        toast.success(message);
        setFile(null);
        setError(null);
    };
    const upload = useWrite(async (f: File) => {
        const body = new FormData();
        body.append('file', f);
        return (
            await api().post<Tenant>(`/staff/tenants/${tenant.id}/logo`, body)
        ).data;
    }, inv);
    const remove = useWrite(
        async () =>
            (await api().delete<Tenant>(`/staff/tenants/${tenant.id}/logo`))
                .data,
        inv,
    );

    return (
        <Card data-testid="tenant-logo">
            <CardHeader>
                <CardTitle className="text-sm font-medium">Logo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                        <p className="text-fg-2 text-xs">In the apps' header</p>
                        <div
                            data-testid="logo-header-preview"
                            className="border-border-1 bg-surface-card flex h-14 items-center rounded-md border px-4"
                        >
                            <TenantLogo brand={brand} />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <p className="text-fg-2 text-xs">On the checkout</p>
                        <TenantTheme
                            brand={brand}
                            className="overflow-hidden rounded-md"
                        >
                            <div
                                data-testid="logo-checkout-preview"
                                className="bg-brand-primary text-brand-fg flex h-14 items-center px-4"
                            >
                                <TenantLogo brand={brand} onBrand />
                            </div>
                        </TenantTheme>
                    </div>
                </div>
                <FileUpload
                    label="New logo"
                    accept={LOGO_TYPES}
                    maxBytes={LOGO_MAX_BYTES}
                    value={file}
                    onChange={(f) => {
                        setFile(f);
                        setError(null);
                    }}
                    error={error}
                    disabled={upload.isPending}
                />
                <div className="flex flex-wrap gap-2">
                    <Button
                        size="sm"
                        disabled={!file || upload.isPending}
                        loading={upload.isPending}
                        onClick={() =>
                            file &&
                            upload.mutate(file, {
                                onSuccess: (next) => done(next, 'Logo saved'),
                                onError: (e) => setError(logoErrorMessage(e)),
                            })
                        }
                    >
                        Upload logo
                    </Button>
                    {logoUrl ? (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setConfirm(true)}
                        >
                            Remove logo
                        </Button>
                    ) : null}
                </div>
            </CardContent>
            <ConfirmDialog
                open={confirm}
                onOpenChange={setConfirm}
                title="Remove the logo?"
                description="The apps and the checkout show the tenant's name instead until a new logo is uploaded."
                confirmLabel="Remove logo"
                destructive
                busy={remove.isPending}
                onConfirm={() =>
                    remove.mutate(undefined, {
                        onSuccess: (next) => {
                            setConfirm(false);
                            done(next, 'Logo removed');
                        },
                        onError: (e) => {
                            setConfirm(false);
                            setError(logoErrorMessage(e));
                        },
                    })
                }
            />
        </Card>
    );
}
